import { MultiLineString } from "geojson";
import { BaseItemResponse } from "../../../base/response/BaseItemResponse";
import { AplicacionDetalleResponse } from "./AplicacionDetalleResponse";
import { AplicacionLoteResponse } from "./AplicacionLoteResponse";

/** Con qué se hizo la aspersión. Es lo que decide cómo se mide la aplicación, y
 *  por eso parte en dos el resto del registro: por **vuelo** (dron) o por
 *  **recipientes en cada lote** (el resto). */
export type MetodoAplicacion =
  | "dron"
  | "estacionaria"
  | "espalda"
  | "tractor"
  | "drench";

/** Si el método se mide con recipientes por lote en vez de con un vuelo. Es la
 *  única pregunta que se le hace al método en casi todo el código: separa
 *  "tiene KML" de "tiene lotes". */
export const fx_esMetodoManual = (metodo: MetodoAplicacion): boolean =>
  metodo !== "dron";

/** Aplicación (aspersión) realizada sobre un cultivo de la finca. */
export type AplicacionResponse = BaseItemResponse & {
  /** Cliente dueño de la aplicación. */
  clienteId: string;
  /** Finca de la aplicación. */
  fincaId: string;
  /** Cultivo asociado (null si no aplica). */
  cultivoId: string | null;
  /** Tratamiento de cultivo asociado (null si no aplica). */
  cultivoTratamientoId: string | null;
  /** Nombre del cultivo (null si no aplica). */
  cultivoNombre: string | null;
  /** Nombre del tratamiento (null si no aplica). */
  cultivoTratamientoNombre: string | null;
  /** Fecha de la aplicación (ISO date). */
  fechaAplicacion: string;
  /** Con qué se aplicó. */
  metodo: MetodoAplicacion;
  /** Trayectoria del vuelo. **`null` en los métodos manuales**: no hay dron, no
   *  hay trayectoria — el mapa pinta los lotes aplicados en su lugar. */
  vuelo: MultiLineString | null;
  /** Área tratada (hectáreas). Del KML en dron; suma del área de los lotes en
   *  manual. */
  taskArea: number;
  /** Volumen total aplicado (litros). Del KML en dron; suma de la mezcla de los
   *  lotes en manual. */
  totalAmount: number;
  /** Descarga por hectárea (opcional). */
  descargaHa?: number;
  /** Productos aplicados con su dosis. */
  detalles: AplicacionDetalleResponse[];

  // --- Solo en los métodos manuales (todo en null si es por dron) ---

  /** Recipiente con el que se midió. Referencia para mostrar su nombre: las
   *  cuentas usan `volumenRecipienteLitros`, que quedó congelado. */
  recipienteId: string | null;
  /** Nombre del recipiente, resuelto por el server. */
  recipienteNombre: string | null;
  /** Litros del recipiente **congelados al guardar**: corregir la caneca de 200
   *  a 180 L no cambia el volumen ya registrado de las aplicaciones anteriores.
   *  Es el factor con el que se recalcula la mezcla de cada lote. */
  volumenRecipienteLitros: number | null;
  /** Mililitros de mezcla por planta (solo drench): el factor que convierte
   *  plantas en litros. */
  mezclaPorPlantaMl: number | null;
  /** Capacidad de la bomba de espalda, en litros (solo espalda). Informativo. */
  capacidadBombaLitros: number | null;
  /** Equipo con el que se aplicó (estacionaria, tractor). Texto libre: no hay
   *  catálogo de equipos. */
  equipo: string | null;
  /** Quién aplicó: persona o cuadrilla. Texto libre, no un usuario del
   *  sistema — el jornalero que fumiga no tiene cuenta. */
  responsable: string | null;
  /** Lotes aplicados con su cantidad. Vacío en dron. */
  lotes: AplicacionLoteResponse[];
};
